/**
 * Milestone_Readiness classification (`complan-governance/requirements.md`
 * Requirement 8).
 *
 * The single definition of whether a required milestone is late, due, missing or
 * done. R8.6 makes this module authoritative for the Detail_Panel, both
 * dashboards, the filter dimension of R6.12 and every export.
 *
 * ## What this replaces
 *
 * `DetailPanelRoute.tsx` currently derives an overdue state inline, by
 * string-comparing dates inside a React component, and its own comment concedes
 * that it is "a presentation reading... nothing is persisted or validated from
 * it". That was correct for Foundation, which reported readiness nowhere. It is
 * not correct once a dashboard counts overdue milestones and a filter selects on
 * them: three readings of "overdue" would exist and could disagree. This module
 * is the one reading, computed on the server; task 16.2 deletes the local one.
 *
 * ## Required_Milestones come from the mandatory flag, never from a type list
 *
 * R8.1: an Initiative's Required_Milestones are the milestone *types* whose master
 * data record carries the mandatory rule flag. This module never names a
 * milestone type. That is what makes R8.11 hold - a changed flag takes effect on
 * the next computation with no recomputation job - and it is also why the parked
 * Roundtable split (Assumption A4) needs no change here: an administrator adding
 * a second roundtable type is picked up automatically.
 *
 * ## The five states partition, exactly
 *
 * {@link classifyRequiredMilestone} returns one member of a closed union and is
 * called once per required milestone, so the five counts sum to the required
 * milestone count by construction (Property 57). The order of the branches is
 * R8.3's order and is not interchangeable: completion wins over lateness (a
 * milestone completed after its planned date is Completed, not Overdue), and
 * absence of any date is Missing rather than Overdue (there is no planned date to
 * be late against).
 */
import { addDays, toCalendarDate, toPlainDate } from '../date/index.js';
import { comparePlainDates } from '../date/plain-date.js';
/** Every {@link ReadinessState}, for exhaustiveness checks and the Property 57 generator. */
export const READINESS_STATES = Object.freeze([
    'completed',
    'overdue',
    'due',
    'missing',
    'planned',
]);
/** The lowest and highest Readiness_Horizon R8.5 accepts, in days. */
export const MIN_READINESS_HORIZON_DAYS = 1;
export const MAX_READINESS_HORIZON_DAYS = 90;
/** R8.5, Assumption A5: the seeded default, matching `governance_readiness_horizon_days`. */
export const DEFAULT_READINESS_HORIZON_DAYS = 14;
/** Whether `days` is a Readiness_Horizon R8.5 permits. */
export function isValidReadinessHorizon(days) {
    return (Number.isInteger(days) &&
        days >= MIN_READINESS_HORIZON_DAYS &&
        days <= MAX_READINESS_HORIZON_DAYS);
}
/**
 * The last date inside the Readiness_Horizon: `cutOff + horizonDays`, inclusive.
 *
 * Exported because the SQL side of the dashboard needs the identical boundary -
 * a `due` count computed in PostgreSQL from a differently-derived horizon date
 * would be the second definition this module exists to prevent.
 */
export function readinessHorizonEnd(cutOff, horizonDays) {
    return toPlainDate(addDays(toCalendarDate(cutOff), horizonDays));
}
/**
 * Classifies one Required_Milestone against `cutOff` (R8.3).
 *
 * Branch order is R8.3's and is load-bearing - see this module's header.
 */
export function classifyRequiredMilestone(milestone, cutOff, horizonDays) {
    // 1. A recorded completion is Completed, whenever it happened. A milestone
    //    completed late is done, not late.
    if (milestone.completedDate !== null) {
        return 'completed';
    }
    // 2. No date at all is Missing (R8.3). Checked before the date comparisons
    //    because they have nothing to compare.
    if (milestone.plannedDate === null) {
        return 'missing';
    }
    // 3. Planned strictly before the cut-off, uncompleted: Overdue. "Precedes" in
    //    R8.3, so a milestone planned *on* the cut-off date is not yet late.
    if (comparePlainDates(milestone.plannedDate, cutOff) < 0) {
        return 'overdue';
    }
    // 4. Planned within the horizon after the cut-off: Due. Inclusive of both the
    //    cut-off date itself and the horizon end.
    if (comparePlainDates(milestone.plannedDate, readinessHorizonEnd(cutOff, horizonDays)) <= 0) {
        return 'due';
    }
    // 5. Recorded, incomplete, beyond the horizon.
    return 'planned';
}
const ZERO_COUNTS = Object.freeze({
    completed: 0,
    overdue: 0,
    due: 0,
    missing: 0,
    planned: 0,
});
/**
 * Summarises one Initiative's Required_Milestones (R8.3, R8.4, R8.8).
 *
 * `allComplete` is false for a not-applicable Initiative: "every required
 * milestone is complete" is vacuously true of an empty set, but reporting an
 * Initiative with no milestones among the complete ones would overstate readiness
 * - which is precisely what R8.8 forbids by requiring a distinct count.
 */
export function summariseReadiness(milestones, cutOff, horizonDays) {
    if (milestones.length === 0) {
        return { counts: ZERO_COUNTS, requiredCount: 0, notApplicable: true, allComplete: false };
    }
    const counts = { ...ZERO_COUNTS };
    for (const milestone of milestones) {
        const state = classifyRequiredMilestone(milestone, cutOff, horizonDays);
        counts[state] += 1;
    }
    return {
        counts: Object.freeze(counts),
        requiredCount: milestones.length,
        notApplicable: false,
        allComplete: counts.completed === milestones.length,
    };
}
/** Exact sum of a {@link ReadinessCounts}, which Property 57 asserts equals the required count. */
export function totalReadinessCount(counts) {
    return counts.completed + counts.overdue + counts.due + counts.missing + counts.planned;
}
/**
 * Adds two {@link ReadinessCounts}, for rolling one Initiative's counts into a
 * Readable_Set total (R3.3, R5.4's funnel).
 */
export function addReadinessCounts(a, b) {
    return Object.freeze({
        completed: a.completed + b.completed,
        overdue: a.overdue + b.overdue,
        due: a.due + b.due,
        missing: a.missing + b.missing,
        planned: a.planned + b.planned,
    });
}
/** The identity of {@link addReadinessCounts}, for a fold over an empty Readable_Set. */
export function emptyReadinessCounts() {
    return ZERO_COUNTS;
}
//# sourceMappingURL=milestone-readiness.js.map